<template>
  <div class="nba-scores-wrap">
    <!-- Header -->
    <div class="nba-scores-header">
      <div class="nba-scores-heading">
        <div class="nba-scores-title-row">
          <span class="nba-scores-icon-wrap" aria-hidden="true">
            <i class="fa fa-basketball"></i>
          </span>
          <span class="nba-scores-title">NBA Intelligence</span>
          <span class="nba-scores-live-badge" v-if="!loading && hasLiveGames">
            <span class="nba-live-pulse"></span> LIVE
          </span>
        </div>
        <span class="nba-scores-kicker">Box score with a take, straight from the barbershop</span>
      </div>
      <div class="nba-scores-meta">
        <span v-if="dateLabel" class="nba-scores-date">
          <i class="fa fa-calendar-days" aria-hidden="true"></i>
          {{ dateLabel }}
        </span>
        <button
          class="nba-scores-toggle"
          @click="expanded = !expanded"
          :aria-expanded="String(expanded)"
          aria-controls="nba-games-list"
        >
          <i :class="expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" aria-hidden="true"></i>
          {{ expanded ? 'Hide' : 'Show' }}
        </button>
      </div>
    </div>

    <div class="nba-date-row">
      <label class="nba-date-label" for="nba-games-date">Date</label>
      <input
        id="nba-games-date"
        v-model="selectedDate"
        class="nba-date-input"
        type="date"
        :max="todayDate"
        @change="loadGames"
      />
    </div>

    <!-- Loading skeleton carousel -->
    <div v-if="loading && expanded" class="carousel-wrapper">
      <div class="carousel-track">
        <div class="carousel-slide" v-for="n in 3" :key="n">
          <div class="nba-skeleton">
            <div class="skel-line skel-line--short"></div>
            <div class="skel-line"></div>
            <div class="skel-line skel-line--med"></div>
            <div class="skel-line skel-line--short"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error && expanded" class="nba-error">
      <i class="fa fa-triangle-exclamation" aria-hidden="true"></i>
      Could not load today's games. Try refreshing.
    </div>

    <!-- No games -->
    <div v-else-if="expanded && !loading && games.length === 0" class="nba-empty">
      <i class="fa fa-moon" aria-hidden="true"></i>
      No NBA games scheduled today.
    </div>

    <!-- Carousel -->
    <div
      v-if="expanded && !loading && games.length"
      class="carousel-wrapper"
      id="nba-games-list"
      aria-live="polite"
    >
      <button
        class="carousel-nav carousel-nav-left"
        @click="scrollCarousel(-1)"
        aria-label="Scroll left"
      >
        <i class="fa fa-chevron-left" aria-hidden="true"></i>
      </button>

      <div
        ref="carouselTrack"
        class="carousel-track"
        role="region"
        aria-label="NBA games carousel"
      >
        <div
          class="carousel-slide"
          v-for="game in games"
          :key="game.gameId"
        >
          <NbaGameCard :game="game" />
        </div>
      </div>

      <button
        class="carousel-nav carousel-nav-right"
        @click="scrollCarousel(1)"
        aria-label="Scroll right"
      >
        <i class="fa fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import NbaGameCard from './NbaGameCard.vue';
import NbaSportsService from '@/services/NbaSportsService';

const POLL_MS = 30000;

function isGameLive(game) {
  const status = String(game?.statusType || game?.status || '').toUpperCase();
  return status === 'LIVE' || status === 'IN';
}

export default {
  name: 'NbaScores',
  components: { NbaGameCard },
  data() {
    const today = new Date().toLocaleDateString('en-CA');
    return {
      games: [],
      loading: false,
      error: false,
      expanded: true,
      dateLabel: '',
      selectedDate: today,
      todayDate: today,
      pollInterval: null,
    };
  },
  computed: {
    hasLiveGames() {
      return this.games.some(isGameLive);
    },
    shouldPoll() {
      return this.selectedDate === this.todayDate
        && this.hasLiveGames
        && this.expanded;
    },
  },
  watch: {
    shouldPoll: {
      immediate: true,
      handler(shouldPoll) {
        if (shouldPoll) this.startPolling();
        else this.stopPolling();
      },
    },
    selectedDate() {
      this.syncPolling();
    },
    expanded() {
      this.syncPolling();
    },
  },
  async mounted() {
    await this.loadGames();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    syncPolling() {
      if (this.shouldPoll) this.startPolling();
      else this.stopPolling();
    },
    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => {
        if (this.shouldPoll) this.loadGames(true);
      }, POLL_MS);
    },
    stopPolling() {
      if (!this.pollInterval) return;
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    },
    async loadGames(silent = false) {
      if (!silent) {
        this.loading = true;
        this.error = false;
      }
      try {
        const response = await NbaSportsService.getGames(this.selectedDate);
        const data = response.data;
        this.games = data?.games || [];
        this.dateLabel = this.formatDateLabel(data?.date || this.selectedDate);
      } catch {
        if (!silent) this.error = true;
      } finally {
        if (!silent) this.loading = false;
      }
    },
    formatDateLabel(dateValue) {
      if (!dateValue) return '';
      try {
        return new Date(`${dateValue}T00:00:00`).toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric',
        });
      } catch {
        return dateValue;
      }
    },
    scrollCarousel(direction) {
      const track = this.$refs.carouselTrack;
      if (!track) return;
      const scrollAmount = track.clientWidth * 0.75;
      track.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.nba-scores-wrap {
  font-family: 'Titillium Web', sans-serif;
  margin-bottom: 16px;
  background: #fffbf5;
  border: 1px solid #fed7aa;
  border-top: 3px solid #f97316;
  border-radius: 14px;
  padding: 14px;
}

/* ── Section header ─────────────────────────────────── */
.nba-scores-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 0 2px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.nba-scores-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nba-scores-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.nba-scores-icon-wrap {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nba-scores-icon-wrap i {
  color: #fff;
  font-size: 14px;
}

.nba-scores-title {
  font-size: 16px;
  font-weight: 900;
  letter-spacing: 0.3px;
  color: #111827;
  line-height: 1;
}

.nba-scores-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1px;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
}
.nba-live-pulse {
  width: 5px;
  height: 5px;
  background: #fff;
  border-radius: 50%;
  animation: pulse-dot 1.2s infinite;
  flex-shrink: 0;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.15; }
}

.nba-scores-kicker {
  font-size: 11px;
  color: #92400e;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.nba-scores-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.nba-scores-date {
  font-size: 11px;
  color: #78350f;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}
.nba-scores-date i { font-size: 10px; }

.nba-scores-toggle {
  background: #f97316;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #fff;
  font-family: inherit;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.15s, transform 0.12s;
  white-space: nowrap;
}
.nba-scores-toggle:hover { background: #ea580c; transform: translateY(-1px); }
.nba-scores-toggle:focus-visible { outline: 2px solid #f97316; outline-offset: 2px; }

.nba-date-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}
.nba-date-label {
  font-size: 11px;
  font-weight: 700;
  color: #78350f;
  text-transform: uppercase;
  letter-spacing: 0.7px;
}
.nba-date-input {
  font-family: inherit;
  font-size: 12px;
  padding: 6px 10px;
  border: 1px solid #fcd9b6;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
}
.nba-date-input:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 1px;
}

/* ── Carousel ────────────────────────────────────────── */
.carousel-wrapper {
  position: relative;
  width: 100%;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: 10px;
  /* side padding leaves room for the nav arrows and peeks the next card */
  padding: 4px 40px 8px;
  scroll-behavior: smooth;
}
.carousel-track::-webkit-scrollbar { display: none; }

.carousel-slide {
  flex: 0 0 auto;
  width: 290px;
  scroll-snap-align: start;
}

/* Nav arrows */
.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffff;
  color: #f97316;
  border: 1.5px solid #fed7aa;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85em;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.carousel-nav:hover {
  background: #f97316;
  color: #fff;
  border-color: #f97316;
  transform: translateY(-50%) scale(1.05);
}
.carousel-nav:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 2px;
}
.carousel-nav-left  { left: 0; }
.carousel-nav-right { right: 0; }

/* ── Skeleton ────────────────────────────────────────── */
.nba-skeleton {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 290px;
}
.skel-line {
  height: 11px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  border-radius: 6px;
  animation: shimmer 1.5s infinite;
}
.skel-line--short { width: 50%; }
.skel-line--med   { width: 75%; }
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Error / empty ───────────────────────────────────── */
.nba-error,
.nba-empty {
  font-size: 13px;
  color: #64748b;
  padding: 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.nba-error i { color: #ef4444; }

/* ── Mobile ──────────────────────────────────────────── */
@media (max-width: 480px) {
  .carousel-slide { width: 260px; }
  .nba-skeleton   { width: 260px; }
  .carousel-track { padding: 4px 32px 8px; }
  .carousel-nav   { width: 30px; height: 30px; font-size: 0.75em; }
}
</style>
